import * as yup from 'yup';
import {
  ADDRESS_GROUP_FIELDS,
  AddressGroupField,
  addressGroupHasContent,
} from './addressGroup';
import { getAddressFieldLabels } from './addressLabels';
import { WORKSPACE_DETAILS_FORM_FIELDS } from './fieldNames';

/** Required non-blank string using the shared validation-message convention. */
export const requiredNonBlankString = (label: string, schema = yup.string()) =>
  schema
    .required(`Please enter ${label}`)
    .test('is-not-empty-or-space', `Please enter valid ${label}`, (value) =>
      Boolean(value?.trim().length),
    );

const siblingsOf = (self: AddressGroupField) =>
  ADDRESS_GROUP_FIELDS.filter((field) => field !== self);

/**
 * Schema for an address-group field that is only required while the group has
 * content (see addressGroupHasContent) — a fully blank address section must
 * not block entity/contact-only saves on legacy workspaces without a street
 * address.
 */
export const addressGroupRequiredString = (
  self: AddressGroupField,
  buildRequired: (schema: yup.StringSchema) => yup.StringSchema,
) =>
  yup
    .string()
    .when(siblingsOf(self), (values, schema: yup.StringSchema) =>
      addressGroupHasContent(values) ? buildRequired(schema) : schema,
    );

/**
 * Group-conditional required string whose validation messages use the
 * per-country address label (City/Suburb, State, Post code, Zip code, ...)
 * resolved from the sibling addressCountry field at validation time.
 */
export const countryAwareRequiredString = (
  labelKey: 'city' | 'postcode' | 'state',
  self: AddressGroupField,
) => {
  const siblings = siblingsOf(self);
  const countryIndex = siblings.indexOf(
    WORKSPACE_DETAILS_FORM_FIELDS.addressCountry,
  );

  return yup.string().when(siblings, (values, schema: yup.StringSchema) => {
    if (!addressGroupHasContent(values)) return schema;
    const label = getAddressFieldLabels(values[countryIndex])[labelKey];

    return requiredNonBlankString(label, schema);
  });
};
